import React from 'react';
import {
	getFormattedCurrency,
	getFormattedDateWithTimestamp,
	getDetailLink,
} from '../../../lib/helpers/index';
import styles from './InvoiceRow.module.scss';
import { LinkObjectType } from '../../../lib/helpers/types';
import { gql, DocumentNode } from '@apollo/client';
import {
	PayInvoicesModalRowInvoiceFragment,
	PayInvoicesModalRowInvoiceForGetContractWhereFragment,
	InstallmentStatus,
} from '#types';
import { Box, ListItem, ListItemText, Typography } from '@mui/material';
import { Button } from '../../Button';
import rpcShared from '@rockpapercoin/rpc-shared';
import { preferredDayJsDateFormat } from '../../../lib/constants';

const payInvoicesModalRowInvoiceFragment = gql`
	fragment payInvoicesModalRowInvoice on Invoice {
		id
		title
		guestToken
		paymentInstallments {
			id
			dueDate
			amountDue
			status
			paymentDate
		}
	}
`;

const payInvoicesModalRowInvoiceForGetContractWhereFragment = gql`
	fragment payInvoicesModalRowInvoiceForGetContractWhere on InvoiceForGetContractWhere {
		id
		title
		guestToken
		paymentInstallments {
			id
			dueDate
			amountDue
			status
			paymentDate
		}
	}
`;

export type InvoiceRowProps = {
	invoice:
	| PayInvoicesModalRowInvoiceFragment
	| PayInvoicesModalRowInvoiceForGetContractWhereFragment;
	onNavigate?: () => any;
};

type InvoiceRowPropsWithFragment = React.FC<InvoiceRowProps> & {
	fragments: {
		payInvoicesModalRowInvoiceFragment: DocumentNode;
		payInvoicesModalRowInvoiceForGetContractWhereFragment: DocumentNode;
	};
};

interface LinkParam {
	token?: string;
}

const InvoiceRow: InvoiceRowPropsWithFragment = ( { invoice, onNavigate } ) => {
	if ( !invoice ) {
		return null;
	}

	const amountOutstanding = rpcShared.invoices.calculateUnpaidBalance(
		invoice.paymentInstallments?.map( ( installment ) => ( {
			status: installment.status,
			amountDue: installment.amountDue,
			paymentDate: installment.paymentDate,
		} ) )
	);

	const linkObject = invoice;
	const linkParams: LinkParam = {
		...( invoice.guestToken ? { token: invoice.guestToken } : {} ),
	};
	const link = getDetailLink( {
		object: linkObject,
		params: linkParams,
		type: LinkObjectType.Invoice,
	} );

	// helper to translate values of Date | string | null | undefined to a number for sorting purposes
	const getTime = ( d?: Date | string | null ) =>
		d ? ( typeof d === 'string' ? new Date( d ).getTime() : d.getTime() ) : 1;

	// get the first unpaid installment due date
	const dueDate = getFormattedDateWithTimestamp(
		invoice.paymentInstallments
			?.sort( ( a, b ) => getTime( a.dueDate ) - getTime( b.dueDate ) )
			?.find( ( installment ) => installment.status === InstallmentStatus.Unpaid )
			?.dueDate,
		preferredDayJsDateFormat
	);

	return (
		<ListItem disableGutters>
			<ListItemText className={ styles.invoiceDetails }>
				<Typography className={ styles.title } component='span'>
					{ invoice.title }
				</Typography>
				<Box component='span' className={ styles.item }>
					<Typography component='span' className={ styles.itemText }>
						Outstanding Payment Due
					</Typography>
					<Typography
						component='span'
						className={ styles.itemText }
						data-label='Invoice Total'
					>
						{ getFormattedCurrency( amountOutstanding, { fromCents: true } ) }
					</Typography>
				</Box>
				<Box component='span' className={ styles.item }>
					<Typography component='span' className={ styles.itemText }>
						Next Installment Due
					</Typography>
					<Typography
						component='span'
						className={ styles.itemText }
						data-label='Next Installment Due'
					>
						{ dueDate }
					</Typography>
				</Box>
			</ListItemText>
			{ link ? (
				<Button
					{ ...link }
					onClick={ onNavigate }
					className={ styles.button }
					data-cy={ `pay-invoice-btn-${ invoice.id }` }
				>
					Pay
				</Button>
			) : null }
		</ListItem>
	);
};

InvoiceRow.fragments = {
	payInvoicesModalRowInvoiceFragment,
	payInvoicesModalRowInvoiceForGetContractWhereFragment,
};

export default InvoiceRow;
